/** @jsx h */
import { component, h, App } from 'literaljs';
import { playPop, playChime } from '../../utils/sounds';
import { copyToClipboard } from '../../utils/clipboard';
import { showToast } from '../../utils/toast';
import SearchBar from '../Learn/SearchBar';
import SkeletonLoader from '../SkeletonLoader';
import QRCode from 'qrcode-generator';

const demoRegistry = {};

function registerDemo(name, component) {
  demoRegistry[name] = component;
}

function getDemo(name) {
  return demoRegistry[name];
}

// ═══════════════════════════════════════════════════
// KANBAN BOARD — Cute task manager
// ═══════════════════════════════════════════════════
const KanbanCard = component({
	name: 'KanbanCard',
	props: ['text', 'priority'],
	render() {
		const priorityColors = {
			high: 'var(--blossom)',
			medium: 'var(--sky)',
			low: 'var(--text-muted)'
		};
		return (
			<div class="kb-card" style={{ borderLeftColor: priorityColors[this.props.priority] || 'var(--text-muted)' }}>
				<span class="kb-card-priority" style={{ color: priorityColors[this.props.priority] || 'var(--text-muted)' }}>{this.props.priority}</span>
				<span class="kb-card-text">{this.props.text}</span>
			</div>
		);
	}
});

const KanbanBoard = component({
	name: 'KanbanBoard',
	state: {
		tasks: [
			{ id: 1, text: 'Design system tokens', column: 'todo', priority: 'high' },
			{ id: 2, text: 'API integration', column: 'todo', priority: 'medium' },
			{ id: 3, text: 'Write unit tests', column: 'todo', priority: 'low' },
			{ id: 4, text: 'Build components', column: 'doing', priority: 'high' },
			{ id: 5, text: 'Code review', column: 'doing', priority: 'medium' },
			{ id: 6, text: 'Setup CI/CD', column: 'done', priority: 'high' },
			{ id: 7, text: 'Deploy v1.0', column: 'done', priority: 'medium' },
		],
		nextId: 8,
		newTask: '',
		newPriority: 'medium',
		editingId: null,
		editText: '',
		draggedTaskId: null,
		dragOverColumn: null
	},
	methods() {
		return {
			moveTask(id, toColumn) {
				this.setState({
					tasks: this.getState().tasks.map(t =>
						t.id === id ? { ...t, column: toColumn } : t
					),
					dragOverColumn: null
				});
			},
			addTask() {
				const { tasks, newTask, newPriority, nextId } = this.getState();
				if (!newTask.trim()) return;
				this.setState({
					tasks: [...tasks, { id: nextId, text: newTask.trim(), column: 'todo', priority: newPriority }],
					nextId: nextId + 1,
					newTask: ''
				});
			},
			deleteTask(id) {
				this.setState({ tasks: this.getState().tasks.filter(t => t.id !== id) });
			},
			updateNewTask(value) { this.setState({ newTask: value }); },
			setPriority(p) { this.setState({ newPriority: p }); },
			startEdit(task) {
				this.setState({ editingId: task.id, editText: task.text });
			},
			saveEdit() {
				const { editingId, editText } = this.getState();
				if (!editText.trim()) return;
				this.setState({
					tasks: this.getState().tasks.map(t =>
						t.id === editingId ? { ...t, text: editText.trim() } : t
					),
					editingId: null,
					editText: ''
				});
			},
			cancelEdit() {
				this.setState({ editingId: null, editText: '' });
			},
			setEditText(text) {
				this.setState({ editText: text });
			},
			handleDragStart(taskId) {
				this.setState({ draggedTaskId: taskId });
			},
			handleDragOver(column) {
				this.setState({ dragOverColumn: column });
			},
			handleDragEnd() {
				const { draggedTaskId, dragOverColumn } = this.getState();
				if (draggedTaskId && dragOverColumn) {
					this.moveTask(draggedTaskId, dragOverColumn);
				}
				this.setState({ draggedTaskId: null, dragOverColumn: null });
			}
		};
	},
	render() {
		const { tasks, newTask, newPriority, editingId, editText, dragOverColumn } = this.getState();
		const columns = [
			{ key: 'todo', label: 'To Do', accent: 'var(--sky)' },
			{ key: 'doing', label: 'In Progress', accent: 'var(--lavender)' },
			{ key: 'done', label: 'Done', accent: 'var(--mint)' }
		];
		return (
			<div class="kb-board">
				<div class="kb-add-row">
					<input
						type="text"
						placeholder="Add a task..."
						value={newTask}
						events={{
							input: (e) => this.updateNewTask(e.target.value),
							keydown: (e) => { if (e.key === 'Enter') this.addTask(); }
						}}
						class="kb-input"
					/>
					<div class="kb-priority-btns">
						{['low', 'medium', 'high'].map(p => (
							<button
								key={p}
								class={"kb-priority-btn" + (newPriority === p ? " -active" : "") + " -" + p}
								events={{ click: () => this.setPriority(p) }}
							>{p}</button>
						))}
					</div>
					<button class="ld-btn -primary" events={{ click: () => this.addTask() }}>+ Add</button>
				</div>
				<div class="kb-columns">
					{columns.map(col => (
						<div
							class={"kb-column" + (dragOverColumn === col.key ? " -drag-over" : "")}
							key={col.key}
							style={{ borderTopColor: col.accent }}
							events={{
								dragover: (e) => { e.preventDefault(); this.handleDragOver(col.key); },
								dragleave: () => this.setState({ dragOverColumn: null }),
								drop: (e) => { e.preventDefault(); this.handleDragEnd(); }
							}}
						>
							<div class="kb-column-header">
								<h4 style={{ color: col.accent }}>{col.label}</h4>
								<span class="kb-count">{tasks.filter(t => t.column === col.key).length}</span>
							</div>
							<div class="kb-cards">
								{tasks.filter(t => t.column === col.key).map(task => (
									<div
										class="kb-task"
										key={task.id}
										style={{ borderLeftColor: task.priority === 'high' ? 'var(--blossom)' : task.priority === 'medium' ? 'var(--sky)' : 'var(--text-muted)' }}
										draggable={true}
										events={{
											dragstart: () => this.handleDragStart(task.id),
											dragend: () => this.handleDragEnd()
										}}
									>
										{editingId === task.id ? (
											<div class="kb-edit-row">
												<input
													type="text"
													value={editText}
													class="kb-edit-input"
													events={{
														input: (e) => this.setEditText(e.target.value),
														keydown: (e) => {
															if (e.key === 'Enter') this.saveEdit();
															if (e.key === 'Escape') this.cancelEdit();
														}
													}}
													autofocus
												/>
												<button class="kb-edit-btn" events={{ click: () => this.saveEdit() }}>Save</button>
												<button class="kb-edit-btn -cancel" events={{ click: () => this.cancelEdit() }}>Cancel</button>
											</div>
										) : (
											<div class="kb-task-content">
												<span class="kb-task-text" events={{ dblclick: () => this.startEdit(task) }}>{task.text}</span>
												<div class="kb-task-actions">
													<button class="kb-move-btn" events={{ click: () => this.startEdit(task) }} title="Edit">Edit</button>
													{col.key !== 'done' && (
														<button class="kb-move-btn" events={{ click: () => this.moveTask(task.id, col.key === 'todo' ? 'doing' : 'done') }} title="Move right">→</button>
													)}
													<button class="kb-del-btn" events={{ click: () => this.deleteTask(task.id) }}>Cancel</button>
												</div>
											</div>
										)}
									</div>
								))}
								</div>
							</div>
						))}
					</div>
				</div>
			);
		}
	});

// ═══════════════════════════════════════════════════
// REAL-TIME SEARCH — Cute searchable API list
// ═══════════════════════════════════════════════════
const SearchDemo = component({
	name: 'SearchDemo',
	state: {
		query: '',
		results: [],
		allItems: [
			{ name: 'component()', cat: 'Core', desc: 'Create reusable components' },
			{ name: 'App', cat: 'Mount', desc: 'Create isolated app instances' },
			{ name: 'getState()', cat: 'State', desc: 'Read component state' },
			{ name: 'setState()', cat: 'State', desc: 'Update component state' },
			{ name: 'getStore()', cat: 'Store', desc: 'Read global store' },
			{ name: 'setStore()', cat: 'Store', desc: 'Update global store' },
			{ name: 'events', cat: 'Events', desc: 'Event delegation prop' },
			{ name: 'mounted()', cat: 'Lifecycle', desc: 'Called after first render' },
			{ name: 'unmounted()', cat: 'Lifecycle', desc: 'Called on removal' },
			{ name: 'h()', cat: 'Core', desc: 'Hyperscript factory for VNodes' },
			{ name: 'Router', cat: 'Router', desc: 'Client-side routing component' },
			{ name: 'navigate()', cat: 'Router', desc: 'Programmatic navigation' }
		]
	},
	methods() {
		return {
			search(query) {
				const q = query.toLowerCase();
				const results = q ? this.getState().allItems.filter(item =>
					item.name.toLowerCase().includes(q) ||
					item.cat.toLowerCase().includes(q) ||
					item.desc.toLowerCase().includes(q)
				) : [];
				this.setState({ query, results });
			}
		};
	},
	render() {
		const { query, results } = this.getState();
		return (
			<div class="search-demo">
				<div class="search-demo-input-wrap">
					<span class="search-demo-icon">Search</span>
					<input
						type="text"
						class="search-demo-input"
						placeholder="Search API methods..."
						value={query}
						events={{ input: (e) => this.search(e.target.value) }}
					/>
				</div>
				{query && (
					<div class="search-demo-results">
						<div class="search-demo-meta">
							{results.length} result{results.length !== 1 ? 's' : ''} for "{query}"
						</div>
						{results.map((item, i) => (
							<div class="search-demo-item" key={i}>
								<span class="search-demo-cat" style={{
									background: item.cat === 'Core' ? 'var(--sky-bg)' :
										item.cat === 'State' ? 'var(--lavender-bg)' :
										item.cat === 'Store' ? 'var(--mint-bg)' :
										item.cat === 'Lifecycle' ? 'var(--blossom-bg)' :
										item.cat === 'Events' ? 'var(--peach-bg)' :
										'rgba(255,255,255,0.05)',
									color: item.cat === 'Core' ? 'var(--sky)' :
										item.cat === 'State' ? 'var(--lavender)' :
										item.cat === 'Store' ? 'var(--mint)' :
										item.cat === 'Lifecycle' ? 'var(--blossom)' :
										item.cat === 'Events' ? 'var(--peach)' :
										'var(--text-muted)'
								}}>{item.cat}</span>
								<span class="search-demo-name">{item.name}</span>
								<span class="search-demo-desc">{item.desc}</span>
							</div>
						))}
						{results.length === 0 && (
							<div class="search-demo-empty">No results. Try "state", "router", or "events". </div>
						)}
					</div>
				)}
			</div>
		);
	}
});

// ═══════════════════════════════════════════════════
// ANALOG CLOCK — Cute canvas clock
// ═══════════════════════════════════════════════════
const ClockDemo = component({
	name: 'ClockDemo',
	state: { time: '' },
	mounted() {
		this._draw();
		this._interval = setInterval(() => this._draw(), 1000);
	},
	unmounted() {
		if (this._interval) clearInterval(this._interval);
	},
	methods() {
		return {
			_draw() {
				const canvas = document.getElementById('demo-clock');
				if (!canvas) return;
				const ctx = canvas.getContext('2d');
				const size = canvas.width;
				const center = size / 2;
				const radius = center - 10;
				const now = new Date();
				const hrs = now.getHours() % 12;
				const mins = now.getMinutes();
				const secs = now.getSeconds();

				ctx.clearRect(0, 0, size, size);

				ctx.beginPath();
				ctx.arc(center, center, radius, 0, Math.PI * 2);
				ctx.fillStyle = 'rgba(139, 92, 246, 0.08)';
				ctx.fill();
				ctx.strokeStyle = 'rgba(167, 139, 250, 0.3)';
				ctx.lineWidth = 2;
				ctx.stroke();

				for (let i = 0; i < 12; i++) {
					const angle = (i * Math.PI / 6) - Math.PI / 2;
					const len = i % 3 === 0 ? 12 : 6;
					const outer = radius - 8;
					const inner = outer - len;
					ctx.beginPath();
					ctx.moveTo(center + Math.cos(angle) * inner, center + Math.sin(angle) * inner);
					ctx.lineTo(center + Math.cos(angle) * outer, center + Math.sin(angle) * outer);
					ctx.strokeStyle = i % 3 === 0 ? 'rgba(167, 139, 250, 0.8)' : 'rgba(167, 139, 250, 0.3)';
					ctx.lineWidth = i % 3 === 0 ? 2 : 1;
					ctx.stroke();
				}

				const hAngle = ((hrs + mins / 60) * Math.PI / 6) - Math.PI / 2;
				ctx.beginPath();
				ctx.moveTo(center, center);
				ctx.lineTo(center + Math.cos(hAngle) * (radius * 0.5), center + Math.sin(hAngle) * (radius * 0.5));
				ctx.strokeStyle = '#c4b5fd';
				ctx.lineWidth = 3;
				ctx.lineCap = 'round';
				ctx.stroke();

				const mAngle = ((mins + secs / 60) * Math.PI / 30) - Math.PI / 2;
				ctx.beginPath();
				ctx.moveTo(center, center);
				ctx.lineTo(center + Math.cos(mAngle) * (radius * 0.7), center + Math.sin(mAngle) * (radius * 0.7));
				ctx.strokeStyle = '#f9a8d4';
				ctx.lineWidth = 2;
				ctx.lineCap = 'round';
				ctx.stroke();

				const sAngle = (secs * Math.PI / 30) - Math.PI / 2;
				ctx.beginPath();
				ctx.moveTo(center, center);
				ctx.lineTo(center + Math.cos(sAngle) * (radius * 0.85), center + Math.sin(sAngle) * (radius * 0.85));
				ctx.strokeStyle = '#6ee7b7';
				ctx.lineWidth = 1;
				ctx.stroke();

				ctx.beginPath();
				ctx.arc(center, center, 3, 0, Math.PI * 2);
				ctx.fillStyle = '#c4b5fd';
				ctx.fill();

				this.setState({ time: now.toLocaleTimeString() });
			}
		};
	},
	render() {
		const { time } = this.getState();
		return (
			<div class="clock-demo">
				<canvas id="demo-clock" width="200" height="200" style={{ width: '200px', height: '200px' }}></canvas>
				<div class="clock-demo-time">{time}</div>
				<div class="clock-demo-legend">
					<span style={{ color: 'var(--lavender)' }}>● Hours</span>
					<span style={{ color: 'var(--blossom)' }}>● Minutes</span>
					<span style={{ color: 'var(--mint)' }}>● Seconds</span>
				</div>
			</div>
		);
	}
});

// ═══════════════════════════════════════════════════
// POMODORO TIMER — Cute productivity timer
// ═══════════════════════════════════════════════════
const PomodoroTimer = component({
	name: 'PomodoroTimer',
	state: {
		timeLeft: 25 * 60,
		isRunning: false,
		mode: 'work',
		sessions: 0
	},
	methods() {
		return {
			toggleTimer() {
				if (this.getState().isRunning) {
					this._stop();
				} else {
					this._start();
				}
			},
			_start() {
				if (this._timer) return;
				this.setState({ isRunning: true });
				this._timer = setInterval(() => {
					const state = this.getState();
					if (!state.isRunning) {
						this._stop();
						return;
					}
					if (state.timeLeft > 0) {
						this.setState({ timeLeft: state.timeLeft - 1 });
					} else {
						this._complete();
					}
				}, 1000);
			},
			_stop() {
				this.setState({ isRunning: false });
				if (this._timer) {
					clearInterval(this._timer);
					this._timer = null;
				}
			},
			reset() {
				this._stop();
				const durations = { work: 25, short: 5, long: 15 };
				this.setState({ timeLeft: durations[this.getState().mode] * 60 });
			},
			setMode(mode) {
				this._stop();
				const durations = { work: 25, short: 5, long: 15 };
				this.setState({ mode, timeLeft: durations[mode] * 60 });
			},
			_complete() {
				this._stop();
				const { mode, sessions } = this.getState();
				let newMode = mode;
				let newSessions = sessions;
				if (mode === 'work') {
					newSessions = sessions + 1;
					newMode = newSessions % 4 === 0 ? 'long' : 'short';
				} else {
					newMode = 'work';
				}
				const durations = { work: 25, short: 5, long: 15 };
				this.setState({
					mode: newMode,
					timeLeft: durations[newMode] * 60,
					sessions: newSessions
				});
			},
			_draw() {
				const canvas = document.getElementById('pomodoro-ring');
				if (!canvas) return;
				const ctx = canvas.getContext('2d');
				const size = canvas.width;
				const center = size / 2;
				const radius = center - 8;
				const { timeLeft, mode } = this.getState();
				const durations = { work: 25, short: 5, long: 15 };
				const total = durations[mode] * 60;
				const progress = ((total - timeLeft) / total) * 100;
				const colors = { work: '#cba6f7', short: '#94e2d5', long: '#89b4fa' };

				ctx.clearRect(0, 0, size, size);

				// Background ring
				ctx.beginPath();
				ctx.arc(center, center, radius, 0, Math.PI * 2);
				ctx.strokeStyle = 'rgba(180, 190, 254, 0.12)';
				ctx.lineWidth = 6;
				ctx.stroke();

				// Progress ring
				const startAngle = -Math.PI / 2;
				const endAngle = startAngle + (progress / 100) * Math.PI * 2;
				ctx.beginPath();
				ctx.arc(center, center, radius, startAngle, endAngle);
				ctx.strokeStyle = colors[mode];
				ctx.lineWidth = 6;
				ctx.lineCap = 'round';
				ctx.stroke();
			}
		};
	},
	mounted() {
		this._draw();
	},
	updated() {
		this._draw();
	},
	unmounted() {
		if (this._timer) {
			clearInterval(this._timer);
			this._timer = null;
		}
	},
	render() {
		const { timeLeft, isRunning, mode, sessions } = this.getState();
		const minutes = Math.floor(timeLeft / 60);
		const seconds = timeLeft % 60;
		const displayMin = String(minutes).padStart(2, '0');
		const displaySec = String(seconds).padStart(2, '0');
		const modeLabels = { work: 'Work', short: 'Short Break', long: 'Long Break' };
		const modeEmojis = { work: 'Work', short: 'Short', long: 'Long' };

		return (
			<div class="pomodoro-demo">
				<div class="pomodoro-ring-wrap">
					<canvas id="pomodoro-ring" width="180" height="180" style={{ width: '180px', height: '180px' }} />
					<div class="pomodoro-time">{displayMin}:{displaySec}</div>
				</div>
				<div class="pomodoro-mode">{modeEmojis[mode]} {modeLabels[mode]}</div>
				<div class="pomodoro-controls">
					<button
						class={"pomodoro-btn" + (isRunning ? " -pause" : " -play")}
						events={{ click: () => this.toggleTimer() }}
					>
						{isRunning ? '⏸ Pause' : '▶ Start'}
					</button>
					<button class="pomodoro-btn -reset" events={{ click: () => this.reset() }}>
						↺ Reset
					</button>
				</div>
				<div class="pomodoro-modes">
					<button
						class={"pomodoro-mode-btn" + (mode === 'work' ? " -active" : "")}
						events={{ click: () => this.setMode('work') }}
					>Work</button>
					<button
						class={"pomodoro-mode-btn" + (mode === 'short' ? " -active" : "")}
						events={{ click: () => this.setMode('short') }}
					>Short</button>
					<button
						class={"pomodoro-mode-btn" + (mode === 'long' ? " -active" : "")}
						events={{ click: () => this.setMode('long') }}
					>Long</button>
				</div>
				<div class="pomodoro-sessions">
					{Array.from({ length: 4 }, (_, i) => (
						<span class={"pomodoro-session-dot" + (i < sessions ? " -done" : "")} key={i} />
					))}
					<span class="pomodoro-session-label">{sessions} session{sessions !== 1 ? 's' : ''}</span>
				</div>
			</div>
		);
	}
});

// ═══════════════════════════════════════════════════
// EXPENSE TRACKER — Budget management
// ═══════════════════════════════════════════════════
const ExpenseTracker = component({
	name: 'ExpenseTracker',
	state: {
		items: [
			{ id: 1, name: 'Coffee', amount: 5.50, category: 'food', date: '2025-05-19' },
			{ id: 2, name: 'Stationery', amount: 12.99, category: 'shopping', date: '2025-05-18' },
			{ id: 3, name: 'Bus fare', amount: 2.75, category: 'transport', date: '2025-05-18' },
			{ id: 4, name: 'Movie ticket', amount: 15.00, category: 'entertainment', date: '2025-05-17' },
		],
		nextId: 5,
		newName: '',
		newAmount: '',
		newCategory: 'food',
		filter: 'all'
	},
	methods() {
		return {
			addExpense() {
				const { items, nextId, newName, newAmount, newCategory } = this.getState();
				const amount = parseFloat(newAmount);
				if (!newName.trim() || isNaN(amount) || amount <= 0) return;
				this.setState({
					items: [...items, {
						id: nextId,
						name: newName.trim(),
						amount,
						category: newCategory,
						date: new Date().toISOString().split('T')[0]
					}],
					nextId: nextId + 1,
					newName: '',
					newAmount: ''
				});
			},
			deleteExpense(id) {
				this.setState({
					items: this.getState().items.filter(i => i.id !== id)
				});
			},
			setFilter(filter) {
				this.setState({ filter });
			},
			getTotal() {
				return this.getState().items.reduce((sum, i) => sum + i.amount, 0).toFixed(2);
			},
			getCategoryTotal(cat) {
				return this.getState().items
					.filter(i => i.category === cat)
					.reduce((sum, i) => sum + i.amount, 0);
			}
		};
	},
	render() {
		const { items, newName, newAmount, newCategory, filter } = this.getState();
		const categories = {
			food: { emoji: 'Food', color: '#fab387' },
			shopping: { emoji: 'Shopping', color: '#cba6f7' },
			transport: { emoji: 'Transport', color: '#89b4fa' },
			entertainment: { emoji: 'Entertainment', color: '#f5c2e7' }
		};
		const filtered = filter === 'all' ? items : items.filter(i => i.category === filter);
		const total = items.reduce((sum, i) => sum + i.amount, 0);

		return (
			<div class="expense-demo">
				<div class="expense-total">
					<span class="expense-total-label">Total Spent</span>
					<span class="expense-total-amount">${total.toFixed(2)}</span>
				</div>

				<div class="expense-breakdown">
					{Object.entries(categories).map(([cat, info]) => {
						const catTotal = items.filter(i => i.category === cat).reduce((s, i) => s + i.amount, 0);
						const pct = total > 0 ? (catTotal / total * 100).toFixed(0) : 0;
						return (
							<div class="expense-bar" key={cat}>
								<div class="expense-bar-fill" style={{ width: pct + '%', background: info.color }} />
								<span class="expense-bar-label">{info.emoji} {cat} ${catTotal.toFixed(2)}</span>
							</div>
						);
					})}
				</div>

				<div class="expense-form">
					<input
						type="text"
						placeholder="Expense name..."
						value={newName}
						events={{ input: (e) => this.setState({ newName: e.target.value }) }}
						class="expense-input"
					/>
					<input
						type="number"
						placeholder="0.00"
						value={newAmount}
						events={{ input: (e) => this.setState({ newAmount: e.target.value }) }}
						class="expense-input"
					/>
					<select
						value={newCategory}
						events={{ change: (e) => this.setState({ newCategory: e.target.value }) }}
						class="expense-select"
					>
						{Object.entries(categories).map(([cat, info]) => (
							<option value={cat} key={cat}>{info.emoji} {cat}</option>
						))}
					</select>
					<button class="expense-add-btn" events={{ click: () => this.addExpense() }}>+ Add</button>
				</div>

				<div class="expense-filters">
					<button
						class={"expense-filter" + (filter === 'all' ? " -active" : "")}
						events={{ click: () => this.setFilter('all') }}
					>All</button>
					{Object.entries(categories).map(([cat, info]) => (
						<button
							class={"expense-filter" + (filter === cat ? " -active" : "")}
							events={{ click: () => this.setFilter(cat) }}
							key={cat}
						>{info.emoji}</button>
					))}
				</div>

				<div class="expense-list">
					{filtered.length === 0 ? (
						<div class="expense-empty">No expenses yet. Add one above! </div>
					) : (
						filtered.map(item => (
							<div class="expense-item" key={item.id}>
								<span class="expense-item-cat">{categories[item.category].emoji}</span>
								<span class="expense-item-name">{item.name}</span>
								<span class="expense-item-amount">${item.amount.toFixed(2)}</span>
								<button
									class="expense-delete"
									events={{ click: () => this.deleteExpense(item.id) }}
								>×</button>
							</div>
						))
					)}
				</div>
			</div>
		);
	}
});

// ═══════════════════════════════════════════════════
// COLOR PALETTE GENERATOR — Design tool
// ═══════════════════════════════════════════════════
const ColorPalette = component({
	name: 'ColorPalette',
	state: {
		colors: [
			{ hex: '#cba6f7', locked: false },
			{ hex: '#f5c2e7', locked: false },
			{ hex: '#89dceb', locked: false },
			{ hex: '#a6e3a1', locked: false },
			{ hex: '#fab387', locked: false }
		],
		copied: null
	},
	methods() {
		return {
			generate() {
				const { colors } = this.getState();
				const newColors = colors.map(c => {
					if (c.locked) return c;
					return { ...c, hex: this._randomColor() };
				});
				this.setState({ colors: newColors });
			},
			_randomColor() {
				const hue = Math.floor(Math.random() * 360);
				const sat = 60 + Math.floor(Math.random() * 30);
				const light = 55 + Math.floor(Math.random() * 25);
				return this._hslToHex(hue, sat, light);
			},
			_hslToHex(h, s, l) {
				s /= 100; l /= 100;
				const k = n => (n + h / 30) % 12;
				const a = s * Math.min(l, 1 - l);
				const f = n => {
					const color = l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
					return Math.round(255 * color).toString(16).padStart(2, '0');
				};
				return `#${f(0)}${f(8)}${f(4)}`;
			},
			toggleLock(index) {
				const colors = [...this.getState().colors];
				colors[index] = { ...colors[index], locked: !colors[index].locked };
				this.setState({ colors });
			},
			copyHex(hex) {
				copyToClipboard(hex,
					() => { showToast(`Copied ${hex}`, { type: 'success' }); playChime(); },
					() => showToast('Failed to copy color', { type: 'error' })
				);
				this.setState({ copied: hex });
				if (this._copyTimer) clearTimeout(this._copyTimer);
				this._copyTimer = setTimeout(() => this.setState({ copied: null }), 1500);
			},
			getContrastColor(hex) {
				const r = parseInt(hex.slice(1, 3), 16);
				const g = parseInt(hex.slice(3, 5), 16);
				const b = parseInt(hex.slice(5, 7), 16);
				const brightness = (r * 299 + g * 587 + b * 114) / 1000;
				return brightness > 128 ? '#1e1e2e' : '#cdd6f4';
			}
		};
	},
	unmounted() {
		if (this._copyTimer) clearTimeout(this._copyTimer);
	},
	render() {
		const { colors, copied } = this.getState();
		return (
			<div class="palette-demo">
				<button class="palette-generate" events={{ click: () => this.generate() }}>
					Generate Palette
				</button>
				<div class="palette-colors">
					{colors.map((color, i) => (
						<div
							class="palette-color"
							key={i}
							style={{ background: color.hex }}
							events={{ click: () => this.copyHex(color.hex) }}
						>
							<div class="palette-overlay">
								<button
									class="palette-lock"
									events={{ click: (e) => { e.stopPropagation(); this.toggleLock(i); } }}
								>
									{color.locked ? 'Lock' : 'Unlock'}
								</button>
								<span class="palette-hex" style={{ color: this.getContrastColor(color.hex) }}>
									{copied === color.hex ? 'Copied! ' : color.hex}
								</span>
							</div>
						</div>
					))}
				</div>
				<p class="palette-hint">Click any color to copy hex • Lock locks a color from regeneration</p>
			</div>
		);
	}
});

// ═══════════════════════════════════════════════════
// MARKDOWN PREVIEWER — Live text formatting
// ═══════════════════════════════════════════════════
const MarkdownPreview = component({
	name: 'MarkdownPreview',
	state: {
		text: `# Hello World!\n\nThis is **bold** and *italic* text.\n\n## Features\n\n- Bullet points\n- Numbered lists\n- **Inline code**\n\n\`\`\`js\nconst hello = "world";\nconsole.log(hello);\n\`\`\`\n\n> A beautiful blockquote\n\n[Links work too!](https://literaljs.com)\n`,
		mode: 'split'
	},
	mounted() {
		this._renderOutput();
	},
	updated() {
		this._renderOutput();
	},
	methods() {
		return {
			parseMarkdown(text) {
				return text
					.replace(/^### (.*$)/gim, '<h3>$1</h3>')
					.replace(/^## (.*$)/gim, '<h2>$1</h2>')
					.replace(/^# (.*$)/gim, '<h1>$1</h1>')
					.replace(/\*\*(.*)\*\*/gim, '<strong>$1</strong>')
					.replace(/\*(.*)\*/gim, '<em>$1</em>')
					.replace(/`([^`]+)`/gim, '<code>$1</code>')
					.replace(/^\> (.*$)/gim, '<blockquote>$1</blockquote>')
					.replace(/^\- (.*$)/gim, '<li>$1</li>')
					.replace(/<li>(.*)<\/li>/gim, '<ul><li>$1</li></ul>')
					.replace(/\[([^\]]+)\]\(([^)]+)\)/gim, '<a href="$2" target="_blank">$1</a>')
					.replace(/\n/gim, '<br>');
			},
			setMode(mode) {
				this.setState({ mode });
			},
			_renderOutput() {
				const output = document.querySelector('.markdown-output');
				if (output) {
					output.innerHTML = this.parseMarkdown(this.getState().text);
				}
			}
		};
	},
	render() {
		const { text, mode } = this.getState();
		return (
			<div class="markdown-demo">
				<div class="markdown-tabs">
					<button class={"markdown-tab" + (mode === 'edit' ? " -active" : "")} events={{ click: () => this.setMode('edit') }}>Edit</button>
					<button class={"markdown-tab" + (mode === 'split' ? " -active" : "")} events={{ click: () => this.setMode('split') }}>Split</button>
					<button class={"markdown-tab" + (mode === 'preview' ? " -active" : "")} events={{ click: () => this.setMode('preview') }}>Preview</button>
				</div>
				<div class={"markdown-panes" + (mode === 'split' ? " -split" : mode === 'edit' ? " -edit" : " -preview")}>
				<textarea
					class="markdown-input"
					events={{ input: (e) => this.setState({ text: e.target.value }) }}
				>{text}</textarea>
					<div class="markdown-output" />
				</div>
			</div>
		);
	}
});

// ═══════════════════════════════════════════════════
// PASSWORD GENERATOR — Security tool
// ═══════════════════════════════════════════════════
const PasswordGenerator = component({
	name: 'PasswordGenerator',
	state: {
		length: 16,
		uppercase: true,
		lowercase: true,
		numbers: true,
		symbols: true,
		password: '',
		strength: 0,
		copied: false
	},
	mounted() {
		this.generate();
	},
	methods() {
		return {
			generate() {
				const { length, uppercase, lowercase, numbers, symbols } = this.getState();
				let chars = '';
				if (lowercase) chars += 'abcdefghijklmnopqrstuvwxyz';
				if (uppercase) chars += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
				if (numbers) chars += '0123456789';
				if (symbols) chars += '!@#$%^&*()_+-=[]{}|;:,.<>?';
				if (!chars) chars = 'abcdefghijklmnopqrstuvwxyz';
				
				let password = '';
				const array = new Uint32Array(length);
				crypto.getRandomValues(array);
				for (let i = 0; i < length; i++) {
					password += chars[array[i] % chars.length];
				}
				this.setState({ password, copied: false });
				this._calcStrength(password);
			},
			_calcStrength(password) {
				let strength = 0;
				if (password.length >= 12) strength += 25;
				else if (password.length >= 8) strength += 15;
				if (/[a-z]/.test(password)) strength += 15;
				if (/[A-Z]/.test(password)) strength += 15;
				if (/[0-9]/.test(password)) strength += 15;
				if (/[^a-zA-Z0-9]/.test(password)) strength += 15;
				this.setState({ strength: Math.min(100, strength) });
			},
			copy() {
				const { password } = this.getState();
				copyToClipboard(password,
					() => { showToast('Password copied to clipboard', { type: 'success' }); playChime(); },
					() => showToast('Failed to copy password', { type: 'error' })
				);
				this.setState({ copied: true });
				if (this._copyTimer) clearTimeout(this._copyTimer);
				this._copyTimer = setTimeout(() => this.setState({ copied: false }), 2000);
			},
			toggleOption(key) {
				this.setState({ [key]: !this.getState()[key] });
				if (this._generateTimer) clearTimeout(this._generateTimer);
				this._generateTimer = setTimeout(() => this.generate(), 0);
			},
			setLength(len) {
				this.setState({ length: parseInt(len) });
				if (this._generateTimer) clearTimeout(this._generateTimer);
				this._generateTimer = setTimeout(() => this.generate(), 0);
			}
		};
	},
	unmounted() {
		if (this._copyTimer) clearTimeout(this._copyTimer);
		if (this._generateTimer) clearTimeout(this._generateTimer);
	},
	render() {
		const { length, uppercase, lowercase, numbers, symbols, password, strength, copied } = this.getState();
		const strengthLabels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
		const strengthIndex = Math.min(4, Math.floor(strength / 20));
		const strengthColors = ['#f38ba8', '#fab387', '#f9e2af', '#a6e3a1', '#94e2d5'];
		const strengthEmojis = ['Weak', 'Fair', 'Good', 'Strong', 'Work'];
		
		return (
			<div class="password-demo">
				<div class="password-display">
					<code class="password-text">{password}</code>
					<button class="password-copy" events={{ click: () => this.copy() }}>
						{copied ? 'Copied!' : 'Copy'}
					</button>
				</div>
				<div class="password-strength">
					<div class="password-strength-bar">
						<div class="password-strength-fill" style={{ width: strength + '%', background: strengthColors[strengthIndex] }} />
					</div>
					<span class="password-strength-label" style={{ color: strengthColors[strengthIndex] }}>
						{strengthEmojis[strengthIndex]} {strengthLabels[strengthIndex]}
					</span>
				</div>
				<div class="password-options">
					<div class="password-slider">
						<label>Length: <strong>{length}</strong></label>
						<input
							type="range"
							min="6"
							max="32"
							value={length}
							events={{ input: (e) => this.setLength(e.target.value) }}
						/>
					</div>
					<div class="password-toggles">
						<label class={"password-toggle" + (uppercase ? " -active" : "")}>
							<input type="checkbox" checked={uppercase} events={{ change: () => this.toggleOption('uppercase') }} />
							<span>Uppercase Uppercase</span>
						</label>
						<label class={"password-toggle" + (lowercase ? " -active" : "")}>
							<input type="checkbox" checked={lowercase} events={{ change: () => this.toggleOption('lowercase') }} />
							<span>Lowercase Lowercase</span>
						</label>
						<label class={"password-toggle" + (numbers ? " -active" : "")}>
							<input type="checkbox" checked={numbers} events={{ change: () => this.toggleOption('numbers') }} />
							<span>Numbers Numbers</span>
						</label>
						<label class={"password-toggle" + (symbols ? " -active" : "")}>
							<input type="checkbox" checked={symbols} events={{ change: () => this.toggleOption('symbols') }} />
							<span> Symbols</span>
						</label>
					</div>
				</div>
				<button class="password-generate" events={{ click: () => this.generate() }}>
					Generate Generate New
				</button>
			</div>
		);
	}
});

// ═══════════════════════════════════════════════════
// TYPING SPEED TEST — WPM calculator
// ═══════════════════════════════════════════════════
const typingQuotes = [
	"The quick brown fox jumps over the lazy dog.",
	"Code is like humor. When you have to explain it, it's bad.",
	"Simplicity is the soul of efficiency.",
	"First, solve the problem. Then, write the code.",
	"Any fool can write code that a computer can understand.",
	"Experience is the name everyone gives to their mistakes.",
	"Make it work, make it right, make it fast."
];

const TypingTest = component({
	name: 'TypingTest',
	state: {
		quote: typingQuotes[0],
		input: '',
		isRunning: false,
		isFinished: false,
		startTime: null,
		wpm: 0,
		accuracy: 100,
		bestWpm: parseInt(localStorage.getItem('literaljs-typing-best') || '0')
	},
	methods() {
		return {
			start() {
				const quote = typingQuotes[Math.floor(Math.random() * typingQuotes.length)];
				this.setState({
					quote,
					input: '',
					isRunning: true,
					isFinished: false,
					startTime: Date.now(),
					wpm: 0,
					accuracy: 100
				});
			},
			onInput(text) {
				const { quote, isRunning, startTime, bestWpm } = this.getState();
				if (!isRunning) return;
				
				const input = text.slice(0, quote.length);
				let correct = 0;
				for (let i = 0; i < input.length; i++) {
					if (input[i] === quote[i]) correct++;
				}
				const accuracy = input.length > 0 ? Math.round((correct / input.length) * 100) : 100;
				
				const elapsed = (Date.now() - startTime) / 1000 / 60; // minutes
				const wpm = elapsed > 0 ? Math.round((input.length / 5) / elapsed) : 0;
				
				if (input === quote) {
					const finalWpm = Math.max(wpm, 0);
					const newBest = Math.max(bestWpm, finalWpm);
					if (newBest > bestWpm) {
						localStorage.setItem('literaljs-typing-best', String(newBest));
					}
					this.setState({
						input,
						isRunning: false,
						isFinished: true,
						wpm: finalWpm,
						accuracy,
						bestWpm: newBest
					});
				} else {
					this.setState({ input, wpm: Math.max(wpm, 0), accuracy });
				}
			},
			reset() {
				const quote = typingQuotes[Math.floor(Math.random() * typingQuotes.length)];
				this.setState({
					quote,
					input: '',
					isRunning: false,
					isFinished: false,
					wpm: 0,
					accuracy: 100
				});
			}
		};
	},
	render() {
		const { quote, input, isRunning, isFinished, wpm, accuracy, bestWpm } = this.getState();
		return (
			<div class="typing-demo">
				<div class="typing-stats">
					<div class="typing-stat">
						<span class="typing-stat-value">{wpm}</span>
						<span class="typing-stat-label">WPM</span>
					</div>
					<div class="typing-stat">
						<span class="typing-stat-value">{accuracy}%</span>
						<span class="typing-stat-label">Accuracy</span>
					</div>
					<div class="typing-stat">
						<span class="typing-stat-value">{bestWpm}</span>
						<span class="typing-stat-label">Best</span>
					</div>
				</div>
				<div class="typing-quote">
					{quote.split('').map((char, i) => {
						let className = 'typing-char';
						if (i < input.length) {
							className += input[i] === char ? ' -correct' : ' -wrong';
						} else if (i === input.length && isRunning) {
							className += ' -cursor';
						}
						return <span class={className} key={i}>{char}</span>;
					})}
				</div>
				{!isRunning && !isFinished && (
					<button class="typing-btn -start" events={{ click: () => this.start() }}>
						 Start Typing Test
					</button>
				)}
				{isRunning && (
					<textarea
						class="typing-input"
						value={input}
						placeholder="Start typing..."
						autofocus
						events={{ input: (e) => this.onInput(e.target.value) }}
					/>
				)}
				{isFinished && (
					<div class="typing-result">
						<div class="typing-result-emoji">{wpm > 60 ? 'Great' : wpm > 40 ? 'Good' : wpm > 20 ? 'Nice' : 'Work'}</div>
						<div class="typing-result-text">
							{wpm > 60 ? 'Amazing speed!' : wpm > 40 ? 'Great job!' : wpm > 20 ? 'Good effort!' : 'Keep practicing!'}
						</div>
						<button class="typing-btn -restart" events={{ click: () => this.reset() }}>
							Retry Try Again
						</button>
					</div>
				)}
			</div>
		);
	}
});

// ═══════════════════════════════════════════════════
// WEATHER WIDGET DATA — Simulated weather for 5 cities
// ═══════════════════════════════════════════════════
const weatherData = {
	London: { temp: 14, condition: 'Rainy', humidity: 82, wind: 18, emoji: 'Rainy' },
	Tokyo: { temp: 22, condition: 'Sunny', humidity: 45, wind: 12, emoji: 'Sunny' },
	'New York': { temp: 18, condition: 'Cloudy', humidity: 60, wind: 22, emoji: 'Cloudy' },
	Paris: { temp: 16, condition: 'Cloudy', humidity: 70, wind: 15, emoji: 'Cloudy' },
	Sydney: { temp: 26, condition: 'Sunny', humidity: 55, wind: 20, emoji: 'Sunny' }
};

const weatherGradients = {
	Sunny: 'linear-gradient(135deg, #f9e2af 0%, #fab387 100%)',
	Cloudy: 'linear-gradient(135deg, #bac2de 0%, #a6adc8 100%)',
	Rainy: 'linear-gradient(135deg, #89b4fa 0%, #b4befe 100%)'
};

// ═══════════════════════════════════════════════════
// ExampleCard — bulletproof code/live toggle
// ═══════════════════════════════════════════════════
const WeatherWidget = component({
	name: 'WeatherWidget',
	state: {
		city: 'London',
		animating: false
	},
	methods() {
		return {
			setCity(city) {
				if (this.getState().city === city) return;
				this.setState({ animating: true });
				setTimeout(() => {
					this.setState({ city, animating: false });
				}, 200);
			}
		};
	},
	render() {
		const { city, animating } = this.getState();
		const data = weatherData[city];
		const cities = Object.keys(weatherData);

		return (
			<div class="weather-demo">
				<div class="weather-card" style={{
					background: weatherGradients[data.condition],
					opacity: animating ? 0.5 : 1,
					transform: animating ? 'scale(0.98)' : 'scale(1)'
				}}>
					<div class="weather-card-header">
						<div>
							<h3 class="weather-card-city">{city}</h3>
							<p class="weather-card-condition">{data.condition}</p>
						</div>
						<div class="weather-card-emoji">{data.emoji}</div>
					</div>
					<div class="weather-card-temp">
						{data.temp}°
					</div>
					<div class="weather-card-meta">
						<div class="weather-card-meta-item">
							<span>Humidity</span> {data.humidity}%
						</div>
						<div class="weather-card-meta-item">
							<span>Wind</span> {data.wind} km/h
						</div>
					</div>
				</div>
				<div class="weather-cities">
					{cities.map(c => (
						<button
							key={c}
							class={"ld-btn -sm" + (city === c ? " -primary" : "")}
							events={{ click: () => this.setCity(c) }}
						>
							{weatherData[c].emoji} {c}
						</button>
					))}
				</div>
			</div>
		);
	}
});

// ═══════════════════════════════════════════════════
// CHART DEMO — Animated bar chart with datasets
// ═══════════════════════════════════════════════════
const chartDatasets = {
	Sales: { data: [45, 72, 38, 91, 56, 67], color: '#cba6f7', label: 'Sales (k)' },
	Users: { data: [120, 195, 150, 280, 210, 245], color: '#89dceb', label: 'Users' },
	Revenue: { data: [12, 28, 18, 42, 31, 35], color: '#a6e3a1', label: 'Revenue (k)' }
};

const ChartDemo = component({
	name: 'ChartDemo',
	state: {
		dataset: 'Sales',
		hoveredIndex: -1,
		animated: true,
		mounted: true
	},
	mounted() {
		// Bars render immediately with proper height
		this.setState({ mounted: true, animated: true });
	},
	methods() {
		return {
			setDataset(name) {
				this.setState({ dataset: name, animated: false });
				setTimeout(() => this.setState({ animated: true }), 50);
			},
			setHovered(index) {
				this.setState({ hoveredIndex: index });
			}
		};
	},
	render() {
		const { dataset, hoveredIndex, animated, mounted } = this.getState();
		const ds = chartDatasets[dataset];
		const max = Math.max(...ds.data);
		const labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];

		return (
			<div class="chart-demo">
				<div class="chart-dataset-btns">
					{Object.keys(chartDatasets).map(name => (
						<button
							key={name}
							class={"ld-btn" + (dataset === name ? " -primary" : "")}
							style={{ fontSize: '12px', padding: '6px 14px' }}
							events={{ click: () => this.setDataset(name) }}
						>
							{name}
						</button>
					))}
				</div>
				<div class="chart-bars-wrap">

					{mounted && ds.data.map((value, i) => {
						const height = Math.max(animated ? (value / max) * 100 : 0, 4);
						return (
							<div
								key={i}
								class="chart-bar-col"
								events={{
									mouseenter: () => this.setHovered(i),
									mouseleave: () => this.setHovered(-1)
								}}
							>
								{hoveredIndex === i && (
									<div class="chart-bar-tooltip">
										{value}
									</div>
								)}
								<div class="chart-bar-fill" style={{
									background: ds.color,
									height: height + '%',
									opacity: hoveredIndex === i ? 0.85 : 1
								}} />
								<span class="chart-bar-label">{labels[i]}</span>
							</div>
						);
					})}
					{!mounted && (
						<div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
							Loading chart...
						</div>
					)}
				</div>
				<div class="chart-dataset-label">
					{ds.label}
				</div>
			</div>
		);
	}
});

// ═══════════════════════════════════════════════════
// IMAGE GALLERY — Grid with filters and lightbox
// ═══════════════════════════════════════════════════
const galleryImages = [
	{ id: 1, src: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop', category: 'nature', title: 'Mountain Peak' },
	{ id: 2, src: 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=400&h=300&fit=crop', category: 'city', title: 'City Skyline' },
	{ id: 3, src: 'https://images.unsplash.com/photo-1433086966358-54859d0ed716?w=400&h=300&fit=crop', category: 'nature', title: 'Waterfall' },
	{ id: 4, src: 'https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=400&h=300&fit=crop', category: 'city', title: 'Urban Streets' },
	{ id: 5, src: 'https://images.unsplash.com/photo-1501854140801-50d01698950b?w=400&h=300&fit=crop', category: 'nature', title: 'Forest Path' },
	{ id: 6, src: 'https://images.unsplash.com/photo-1514565131-fce0801e5785?w=400&h=300&fit=crop', category: 'city', title: 'Night City' }
];

const ImageGallery = component({
	name: 'ImageGallery',
	state: {
		filter: 'all',
		lightbox: null,
		loaded: {}
	},
	mounted() {
		this._onKeydown = (e) => {
			if (e.key === 'Escape' && this.getState().lightbox !== null) {
				this.closeLightbox();
			}
		};
		document.addEventListener('keydown', this._onKeydown);
	},
	unmounted() {
		if (this._onKeydown) {
			document.removeEventListener('keydown', this._onKeydown);
		}
	},
	methods() {
		return {
			setFilter(filter) {
				this.setState({ filter });
			},
			openLightbox(id) {
				this.setState({ lightbox: id });
			},
			closeLightbox() {
				this.setState({ lightbox: null });
			},
			markLoaded(id) {
				this.setState({ loaded: { ...this.getState().loaded, [id]: true } });
			}
		};
	},
	render() {
		const { filter, lightbox, loaded } = this.getState();
		const filtered = filter === 'all' ? galleryImages : galleryImages.filter(img => img.category === filter);
		const lightboxImg = galleryImages.find(img => img.id === lightbox);

		return (
			<div class="gallery-demo">
				<div class="gallery-filters">
					{['all', 'nature', 'city'].map(f => (
						<button
							key={f}
							class={"ld-btn" + (filter === f ? " -primary" : "")}
							style={{ fontSize: '12px', padding: '6px 14px', textTransform: 'capitalize' }}
							events={{ click: () => this.setFilter(f) }}
						>
							{f === 'all' ? 'All' : f === 'nature' ? 'Nature' : 'City'}
						</button>
					))}
				</div>
				<div class="gallery-grid">
					{filtered.map(img => (
						<div
							key={img.id}
							class="gallery-item"
							events={{ click: () => this.openLightbox(img.id) }}
						>
							{!loaded[img.id] && (
								<div class="gallery-shimmer" />
							)}
							<img
								src={img.src}
								alt={img.title}
								class="gallery-img"
								style={{ display: loaded[img.id] ? 'block' : 'none' }}
								events={{ load: () => this.markLoaded(img.id) }}
							/>
							<div class="gallery-img-title" style={{ opacity: loaded[img.id] ? 1 : 0 }}>
								{img.title}
							</div>
						</div>
					))}
				</div>
				{lightboxImg && (
					<div
						class="gallery-lightbox"
						role="dialog"
						aria-modal="true"
						aria-label="Image lightbox"
						events={{ click: () => this.closeLightbox() }}
					>
						<button
							class="gallery-lightbox-close"
							aria-label="Close lightbox"
							events={{ click: () => this.closeLightbox() }}
						>
							Cancel
						</button>
						<img
							src={lightboxImg.src.replace('w=400&h=300', 'w=800&h=600')}
							alt={lightboxImg.title}
							class="gallery-lightbox-img"
						/>
					</div>
				)}
			</div>
		);
	}
});

// ═══════════════════════════════════════════════════
// CHAT INTERFACE — Mock chat with bot responses
// ═══════════════════════════════════════════════════
const botResponses = [
	"That's interesting! Tell me more.",
	"I see what you mean. Have you tried LiteralJS?",
	"Great question! The answer is 42.",
	"I'm just a demo bot, but you seem cool! ",
	"Did you know LiteralJS has zero dependencies?",
	"Thanks for chatting! How's your day going?",
	"Fascinating! I love learning new things.",
	"Have you checked out the examples page?"
];

const ChatInterface = component({
	name: 'ChatInterface',
	unmounted() {
		if (this._typingTimeout) clearTimeout(this._typingTimeout);
	},
	state: {
		messages: [
			{ id: 1, text: 'Hey there! Welcome to the LiteralJS demo. Hi', sender: 'bot', time: '10:00 AM' },
			{ id: 2, text: 'This chat is built with component() and state!', sender: 'bot', time: '10:00 AM' },
			{ id: 3, text: 'Type a message and I will respond.', sender: 'user', time: '10:01 AM' }
		],
		input: '',
		isTyping: false,
		nextId: 4
	},
	methods() {
		return {
			sendMessage() {
				const { input, messages, nextId } = this.getState();
				if (!input.trim()) return;
				const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
				this.setState({
					messages: [...messages, { id: nextId, text: input.trim(), sender: 'user', time: now }],
					input: '',
					isTyping: true,
					nextId: nextId + 1
				});
				this._typingTimeout = setTimeout(() => {
					const response = botResponses[Math.floor(Math.random() * botResponses.length)];
					const responseTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
					this.setState({
											messages: [...this.getState().messages, { id: this.getState().nextId, text: response, sender: 'bot', time: responseTime }],
											isTyping: false,
											nextId: this.getState().nextId + 1
										});
										requestAnimationFrame(() => {
											const chatMessages = document.querySelector('.chat-messages');
											if (chatMessages) chatMessages.scrollTop = chatMessages.scrollHeight;
										});
				}, 1500 + Math.random() * 1000);
			},
			setInput(value) {
				this.setState({ input: value });
			}
		};
	},
	render() {
		const { messages, input, isTyping } = this.getState();

		return (
			<div class="chat-demo">
				<div class="chat-messages" role="log" aria-live="polite" aria-atomic="false">
					{messages.map(msg => (
						<div
							key={msg.id}
							class={"chat-message " + (msg.sender === 'user' ? '-user' : '-bot')}
							aria-label={`${msg.sender} message`}
						>
							<div>{msg.text}</div>
							<div class="chat-message-time">{msg.time}</div>
						</div>
					))}
					{isTyping && (
						<div class="chat-typing" aria-label="Bot is typing">
							{[0, 1, 2].map(i => (
								<span
									key={i}
									class="chat-typing-dot"
									style={{ animation: `bounce 1.4s infinite ease-in-out ${i * 0.16}s` }}
								/>
							))}
						</div>
					)}
				</div>
				<div class="chat-input-row">
					<input
						type="text"
						placeholder="Type a message..."
						value={input}
						class="chat-input"
						events={{
							input: (e) => this.setInput(e.target.value),
							keydown: (e) => { if (e.key === 'Enter') this.sendMessage(); }
						}}
					/>
					<button
						class="ld-btn -primary chat-send-btn"
						events={{ click: () => this.sendMessage() }}
					>
						Send
					</button>
				</div>
			</div>
		);
	}
});

// ═══════════════════════════════════════════════════
// CALCULATOR — Simple arithmetic calculator
// ═══════════════════════════════════════════════════
const Calculator = component({
	name: 'Calculator',
	state: {
		display: '0',
		previousValue: null,
		operation: null,
		waitingForOperand: false
	},
	methods() {
		return {
			inputDigit(digit) {
				const { display, waitingForOperand } = this.getState();
				if (waitingForOperand) {
					this.setState({ display: String(digit), waitingForOperand: false });
				} else {
					this.setState({ display: display === '0' ? String(digit) : display + digit });
				}
			},
			inputDot() {
				const { display, waitingForOperand } = this.getState();
				if (waitingForOperand) {
					this.setState({ display: '0.', waitingForOperand: false });
				} else if (display.indexOf('.') === -1) {
					this.setState({ display: display + '.' });
				}
			},
			clear() {
				this.setState({ display: '0', previousValue: null, operation: null, waitingForOperand: false });
			},
			performOperation(nextOperation) {
				const { display, previousValue, operation } = this.getState();
				const inputValue = parseFloat(display);
				if (previousValue === null) {
					this.setState({ previousValue: inputValue, waitingForOperand: true, operation: nextOperation });
				} else if (operation) {
					const currentValue = previousValue || 0;
					const newValue = this.calculate(currentValue, inputValue, operation);
					this.setState({ display: String(newValue), previousValue: newValue, waitingForOperand: true, operation: nextOperation });
				}
			},
			calculate(a, b, op) {
				switch (op) {
					case '+': return a + b;
					case '-': return a - b;
					case '×': return a * b;
					case '÷': return b !== 0 ? a / b : 0;
					default: return b;
				}
			}
		};
	},
	render() {
		const { display } = this.getState();
		return (
			<div class="calc-demo">
				<div class="calc-display">{display}</div>
				<div class="calc-buttons">
					<button class="calc-btn -clear" events={{ click: () => this.clear() }}>C</button>
					<button class="calc-btn -op" events={{ click: () => this.performOperation('÷') }}>÷</button>
					<button class="calc-btn -op" events={{ click: () => this.performOperation('×') }}>×</button>
					<button class="calc-btn -op" events={{ click: () => this.performOperation('-') }}>-</button>
					<button class="calc-btn" events={{ click: () => this.inputDigit(7) }}>7</button>
					<button class="calc-btn" events={{ click: () => this.inputDigit(8) }}>8</button>
					<button class="calc-btn" events={{ click: () => this.inputDigit(9) }}>9</button>
					<button class="calc-btn -op" events={{ click: () => this.performOperation('+') }}>+</button>
					<button class="calc-btn" events={{ click: () => this.inputDigit(4) }}>4</button>
					<button class="calc-btn" events={{ click: () => this.inputDigit(5) }}>5</button>
					<button class="calc-btn" events={{ click: () => this.inputDigit(6) }}>6</button>
					<button class="calc-btn -equals" events={{ click: () => this.performOperation('=') }}>=</button>
					<button class="calc-btn" events={{ click: () => this.inputDigit(1) }}>1</button>
					<button class="calc-btn" events={{ click: () => this.inputDigit(2) }}>2</button>
					<button class="calc-btn" events={{ click: () => this.inputDigit(3) }}>3</button>
					<button class="calc-btn -zero" events={{ click: () => this.inputDigit(0) }}>0</button>
					<button class="calc-btn" events={{ click: () => this.inputDot() }}>.</button>
				</div>
			</div>
		);
	}
});

// ═══════════════════════════════════════════════════
// MUSIC PLAYER
// ═══════════════════════════════════════════════════
const MusicPlayer = component({
	name: 'MusicPlayer',
	state: {
		isPlaying: false,
		currentTrack: 0,
		progress: 0
	},
	mounted() {
		this._progressInterval = setInterval(() => {
			if (this.getState().isPlaying) {
				const { progress } = this.getState();
				if (progress >= 100) {
					this.nextTrack();
				} else {
					this.setState({ progress: progress + 0.5 });
				}
			}
		}, 500);
	},
	unmounted() {
		if (this._progressInterval) clearInterval(this._progressInterval);
	},
	methods() {
		return {
			togglePlay() {
				const { isPlaying } = this.getState();
				this.setState({ isPlaying: !isPlaying });
			},
			selectTrack(index) {
				const wasPlaying = this.getState().isPlaying;
				this.setState({ currentTrack: index, progress: 0 });
				if (wasPlaying) {
					this.setState({ isPlaying: true });
				}
			},
			nextTrack() {
				const { currentTrack } = this.getState();
				const wasPlaying = this.getState().isPlaying;
				this.setState({ currentTrack: (currentTrack + 1) % 5, progress: 0 });
				if (wasPlaying) {
					this.setState({ isPlaying: true });
				}
			},
			prevTrack() {
				const { currentTrack } = this.getState();
				const wasPlaying = this.getState().isPlaying;
				this.setState({ currentTrack: (currentTrack - 1 + 5) % 5, progress: 0 });
				if (wasPlaying) {
					this.setState({ isPlaying: true });
				}
			}
		};
	},
	render() {
		const { isPlaying, currentTrack, progress } = this.getState();
		const tracks = [
			{ title: 'Midnight City', artist: 'M83', duration: '4:03' },
			{ title: 'Blinding Lights', artist: 'The Weeknd', duration: '3:20' },
			{ title: 'Levitating', artist: 'Dua Lipa', duration: '3:23' },
			{ title: 'Heat Waves', artist: 'Glass Animals', duration: '3:58' },
			{ title: 'Stay', artist: 'Kid LAROI', duration: '2:21' }
		];
		const track = tracks[currentTrack];
		return (
			<div class="music-demo">
				<div class="music-album">
					<div class="music-album-art" style={{ animation: isPlaying ? 'spin 8s linear infinite' : 'none' }}>
						Music
					</div>
					<div class="music-info">
						<div class="music-title">{track.title}</div>
						<div class="music-artist">{track.artist}</div>
					</div>
				</div>
				<div class="music-progress">
					<div class="music-progress-bar">
						<div class="music-progress-fill" style={{ width: progress + '%' }} />
					</div>
					<div class="music-time">
						<span>{Math.floor(progress * 0.04)}:{String(Math.floor((progress * 0.04 % 1) * 60)).padStart(2, '0')}</span>
						<span>{track.duration}</span>
					</div>
				</div>
				<div class="music-controls">
					<button class="music-btn" events={{ click: () => this.prevTrack() }}>⏮</button>
					<button class="music-btn -play" events={{ click: () => this.togglePlay() }}>
						{isPlaying ? '⏸' : '▶'}
					</button>
					<button class="music-btn" events={{ click: () => this.nextTrack() }}>⏭</button>
				</div>
				<div class="music-playlist">
					{tracks.map((t, i) => (
						<div
							key={i}
							class={"music-track" + (i === currentTrack ? " -active" : "")}
							events={{ click: () => this.selectTrack(i) }}
						>
							<span>{i === currentTrack ? (isPlaying ? '▶' : '⏸') : (i + 1)}</span>
							<span>{t.title}</span>
							<span>{t.duration}</span>
						</div>
					))}
				</div>
			</div>
		);
	}
});

// ═══════════════════════════════════════════════════
// QR CODE GENERATOR
// ═══════════════════════════════════════════════════
const QRGenerator = component({
	name: 'QRGenerator',
	state: {
		text: 'https://literaljs.com',
		qrData: null
	},
	mounted() {
		this.generateQR();
	},
	methods() {
		return {
			setText(text) {
				this.setState({ text });
			},
			generateQR() {
				const { text } = this.getState();
				const canvas = document.getElementById('qr-canvas');
				if (!canvas) return;
				const ctx = canvas.getContext('2d');
				const size = 200;
				canvas.width = size;
				canvas.height = size;
				ctx.fillStyle = '#fff';
				ctx.fillRect(0, 0, size, size);

				const qr = this._generateQRData(text);
				const cellSize = size / qr.size;
				for (let i = 0; i < qr.size; i++) {
					for (let j = 0; j < qr.size; j++) {
						if (qr.data[i * qr.size + j]) {
							ctx.fillStyle = '#1e1e2e';
							ctx.fillRect(j * cellSize, i * cellSize, cellSize, cellSize);
						}
					}
				}
			},
			_generateQRData(text) {
				const typeNumber = 0;
				const errorCorrectionLevel = 'L';
				const qr = QRCode(typeNumber, errorCorrectionLevel);
				qr.addData(text);
				qr.make();
				const size = qr.getModuleCount();
				const data = [];
				for (let i = 0; i < size; i++) {
					for (let j = 0; j < size; j++) {
						data.push(qr.isDark(i, j) ? 1 : 0);
					}
				}
				return { size, data };
			}
		};
	},
	render() {
		const { text } = this.getState();
		return (
			<div class="qr-demo">
				<div class="qr-input-row">
					<input
						type="text"
						value={text}
						placeholder="Enter text or URL..."
						class="qr-input"
						events={{ input: (e) => this.setText(e.target.value), keydown: (e) => e.key === 'Enter' && this.generateQR() }}
					/>
					<button class="qr-generate-btn" events={{ click: () => this.generateQR() }}>
						Generate
					</button>
				</div>
				<div class="qr-canvas-wrap">
					<canvas id="qr-canvas" style={{ width: '200px', height: '200px' }} />
				</div>
				<p class="qr-hint">Enter text and press Enter or click Generate</p>
			</div>
		);
	}
});

registerDemo('kanban', KanbanBoard);
registerDemo('search', SearchDemo);
registerDemo('clock', ClockDemo);
registerDemo('pomodoro', PomodoroTimer);
registerDemo('expense', ExpenseTracker);
registerDemo('palette', ColorPalette);
registerDemo('markdown', MarkdownPreview);
registerDemo('password', PasswordGenerator);
registerDemo('typing', TypingTest);
registerDemo('weather', WeatherWidget);
registerDemo('chart', ChartDemo);
registerDemo('gallery', ImageGallery);
registerDemo('chat', ChatInterface);
registerDemo('calculator', Calculator);
registerDemo('music', MusicPlayer);
registerDemo('qr', QRGenerator);

// TODO LIST
const TodoList = component({
	name: 'TodoList',
	state: {
		todos: [
			{ id: 1, text: 'Learn LiteralJS basics', done: true, priority: 'high' },
			{ id: 2, text: 'Build a component', done: false, priority: 'medium' },
			{ id: 3, text: 'Deploy to production', done: false, priority: 'high' },
			{ id: 4, text: 'Write documentation', done: false, priority: 'low' }
		],
		nextId: 5,
		newTodo: '',
		newPriority: 'medium',
		filter: 'all',
		editingId: null,
		editText: ''
	},
	methods() {
		return {
			toggleTodo(id) {
				const todos = this.getState().todos.map(t =>
					t.id === id ? { ...t, done: !t.done } : t
				);
				this.setState({ todos });
			},
			addTodo() {
				const { newTodo, newPriority, todos, nextId } = this.getState();
				if (!newTodo.trim()) return;
				this.setState({
					todos: [...todos, { id: nextId, text: newTodo.trim(), done: false, priority: newPriority }],
					nextId: nextId + 1,
					newTodo: ''
				});
			},
			setNewTodo(text) { this.setState({ newTodo: text }); },
			setPriority(p) { this.setState({ newPriority: p }); },
			removeTodo(id) {
				this.setState({
					todos: this.getState().todos.filter(t => t.id !== id)
				});
			},
			setFilter(filter) { this.setState({ filter }); },
			startEdit(todo) {
				this.setState({ editingId: todo.id, editText: todo.text });
			},
			saveEdit() {
				const { editingId, editText } = this.getState();
				if (!editText.trim()) return;
				this.setState({
					todos: this.getState().todos.map(t =>
						t.id === editingId ? { ...t, text: editText.trim() } : t
					),
					editingId: null,
					editText: ''
				});
			},
			cancelEdit() {
				this.setState({ editingId: null, editText: '' });
			},
			setEditText(text) { this.setState({ editText: text }); }
		};
	},
	render() {
		const { todos, newTodo, newPriority, filter, editingId, editText } = this.getState();
		const filtered = filter === 'all' ? todos :
			filter === 'active' ? todos.filter(t => !t.done) :
			filter === 'completed' ? todos.filter(t => t.done) :
			todos.filter(t => t.priority === filter);
		const completed = todos.filter(t => t.done).length;
		const total = todos.length;
		const priorityColors = { high: 'var(--blossom)', medium: 'var(--sky)', low: 'var(--text-muted)' };

		return (
			<div class="todo-demo">
				<div class="todo-input-row">
					<input
						type="text"
						value={newTodo}
						placeholder="Add a new task..."
						class="todo-input"
						events={{
							input: (e) => this.setNewTodo(e.target.value),
							keydown: (e) => e.key === 'Enter' && this.addTodo()
						}}
					/>
					<div class="todo-priority-btns">
						{['low', 'medium', 'high'].map(p => (
							<button
								key={p}
								class={"todo-priority-btn" + (newPriority === p ? " -active" : "") + " -" + p}
								events={{ click: () => this.setPriority(p) }}
							>{p}</button>
						))}
					</div>
					<button class="ld-btn -primary" events={{ click: () => this.addTodo() }}>+ Add</button>
				</div>
				<div class="todo-filters">
					{['all', 'active', 'completed', 'high', 'medium', 'low'].map(f => (
						<button
							key={f}
							class={"todo-filter" + (filter === f ? " -active" : "")}
							events={{ click: () => this.setFilter(f) }}
						>
							{f === 'all' ? 'All' : f === 'active' ? '⏳ Active' : f === 'completed' ? 'Done' : f === 'high' ? 'High' : f === 'medium' ? 'Medium Med' : 'Low'}
						</button>
					))}
				</div>
				<div class="todo-list">
					{filtered.map(todo => (
						<div key={todo.id} class={"todo-item" + (todo.done ? " -done" : "")}>
							<input
								type="checkbox"
								{...(todo.done ? { checked: true } : {})}
								events={{ change: () => this.toggleTodo(todo.id) }}
							/>
							{editingId === todo.id ? (
								<div class="todo-edit-row">
									<input
										type="text"
										value={editText}
										class="todo-edit-input"
										events={{
											input: (e) => this.setEditText(e.target.value),
											keydown: (e) => {
												if (e.key === 'Enter') this.saveEdit();
												if (e.key === 'Escape') this.cancelEdit();
											}
										}}
										autofocus
									/>
									<button class="todo-edit-btn" events={{ click: () => this.saveEdit() }}>Save</button>
									<button class="todo-edit-btn -cancel" events={{ click: () => this.cancelEdit() }}>Cancel</button>
								</div>
							) : (
								<div class="todo-item-content" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
									<span class="todo-text" events={{ dblclick: () => this.startEdit(todo) }}>{todo.text}</span>
									<span class="todo-priority-dot" style={{ background: priorityColors[todo.priority] }} title={todo.priority}></span>
									<div class="todo-actions">
										<button class="todo-action-btn" events={{ click: () => this.startEdit(todo) }} title="Edit">Edit</button>
										<button class="todo-action-btn -delete" events={{ click: () => this.removeTodo(todo.id) }} title="Delete">Cancel</button>
									</div>
								</div>
							)}
						</div>
					))}
					{filtered.length === 0 && (
						<div class="todo-empty">No tasks here. Add one above! </div>
					)}
				</div>
				<div class="todo-stats">
					<span>{completed} / {total} completed</span>
					<div class="todo-progress">
						<div class="todo-progress-fill" style={{ width: total > 0 ? (completed / total * 100) + '%' : '0%' }} />
					</div>
				</div>
			</div>
		);
	}
});

// CODE SNIPPET
const CodeSnippet = component({
	name: 'CodeSnippet',
	state: {
		code: `const greeting = 'Hello, LiteralJS!';

component({
  name: 'HelloWorld',
  render() {
    return <h1>{greeting}</h1>;
  }
});`,
		language: 'javascript'
	},
	methods() {
		return {
			setCode(code) { this.setState({ code }); },
			setLanguage(language) { this.setState({ language }); }
		};
	},
	render() {
		const { code, language } = this.getState();
		const languages = [
			{ id: 'javascript', label: 'JavaScript' },
			{ id: 'jsx', label: 'JSX' },
			{ id: 'html', label: 'HTML' },
			{ id: 'css', label: 'CSS' }
		];

		const highlight = (text, lang) => {
			if (lang === 'javascript' || lang === 'jsx') {
				const tokens = [];
				const regex = /(\b(?:const|let|var|function|return|if|else|for|while|class|import|export|from|default|async|await)\b)|(\b(?:component|name|state|methods|render|mounted|unmounted)\b)|('[^']*'|"[^"]*")|(\b\d+\b)|(\/\/.*)|(<[^>]+>)|(&)/g;
				let lastIndex = 0;
				let match;

				while ((match = regex.exec(text)) !== null) {
					if (match.index > lastIndex) {
						tokens.push(text.slice(lastIndex, match.index));
					}

					if (match[1]) {
						tokens.push(<span class="token-keyword">{match[1]}</span>);
					} else if (match[2]) {
						tokens.push(<span class="token-function">{match[2]}</span>);
					} else if (match[3]) {
						tokens.push(<span class="token-string">{match[3]}</span>);
					} else if (match[4]) {
						tokens.push(<span class="token-number">{match[4]}</span>);
					} else if (match[5]) {
						tokens.push(<span class="token-comment">{match[5]}</span>);
					} else if (match[6]) {
						tokens.push(<span class="token-string">{match[6]}</span>);
					} else if (match[7]) {
						tokens.push('&');
					}

					lastIndex = regex.lastIndex;
				}

				if (lastIndex < text.length) {
					tokens.push(text.slice(lastIndex));
				}

				return tokens;
			}
			return text;
		};

		return (
			<div class="snippet-demo">
				<div class="snippet-header">
					<div class="snippet-lang-btns">
						{languages.map(lang => (
							<button
								key={lang.id}
								class={"snippet-lang-btn" + (language === lang.id ? " -active" : "")}
								events={{ click: () => this.setLanguage(lang.id) }}
							>
								{lang.label}
							</button>
						))}
					</div>
					<button
						class="snippet-copy"
						events={{ click: () => {
							copyToClipboard(code,
								() => { showToast('Code copied to clipboard', { type: 'success' }); playChime(); },
								() => showToast('Failed to copy code', { type: 'error' })
							);
							this.setState({ copied: true });
							setTimeout(() => this.setState({ copied: false }), 1500);
						}}}
					>
						{this.getState().copied ? 'Copied!' : 'Copy'}
					</button>
				</div>
				<pre class="snippet-code">
					<code class={"language-" + language}>{highlight(code, language)}</code>
				</pre>
				<textarea
					class="snippet-editor"
					rows="8"
					events={{ input: (e) => this.setCode(e.target.value) }}
					placeholder="Enter code here..."
				>{code}</textarea>
			</div>
		);
	}
});


const DataViz = component({
	name: 'DataViz',
	state: {
		chartType: 'bar',
		data: [
			{ label: 'Jan', value: 45 },
			{ label: 'Feb', value: 72 },
			{ label: 'Mar', value: 38 },
			{ label: 'Apr', value: 91 },
			{ label: 'May', value: 56 },
			{ label: 'Jun', value: 67 }
		],
		hoveredIndex: -1
	},
	methods() {
		return {
			setChartType(type) { this.setState({ chartType: type }); },
			setHovered(index) { this.setState({ hoveredIndex: index }); },
			updateValue(index, newValue) {
				const data = [...this.getState().data];
				data[index] = { ...data[index], value: parseInt(newValue) || 0 };
				this.setState({ data });
			}
		};
	},
	render() {
		const { chartType, data, hoveredIndex } = this.getState();
		const max = Math.max(...data.map(d => d.value));
		const colors = ['#cba6f7', '#f5c2e7', '#89dceb', '#a6e3a1', '#fab387', '#b4befe'];

		return (
			<div class="viz-demo">
				<div class="viz-controls">
					<div class="viz-type-btns">
						{['bar', 'line', 'pie'].map(type => (
							<button
								key={type}
								class={"viz-type-btn" + (chartType === type ? " -active" : "")}
								events={{ click: () => this.setChartType(type) }}
							>
								{type === 'bar' ? 'Bar' : type === 'line' ? 'Line' : 'Pie'}
							</button>
						))}
					</div>
				</div>
				<div class="viz-chart">
					{chartType === 'bar' && (
						<div class="viz-bar-chart">
							{data.map((d, i) => (
								<div
									key={i}
									class="viz-bar-col"
									events={{
										mouseenter: () => this.setHovered(i),
										mouseleave: () => this.setHovered(-1)
									}}
								>
									{hoveredIndex === i && (
										<div class="viz-tooltip">{d.value}</div>
									)}
									<div
										class="viz-bar"
										style={{
											height: (d.value / max * 100) + '%',
											background: colors[i % colors.length]
										}}
									/>
									<span class="viz-label">{d.label}</span>
								</div>
							))}
						</div>
					)}
					{chartType === 'line' && (
						<svg viewBox="0 0 300 150" class="viz-line-chart" width="100%" height="200">
							<defs>
								<linearGradient id="lineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
									<stop offset="0%" stop-color="#cba6f7" stop-opacity="0.3" />
									<stop offset="100%" stop-color="#cba6f7" stop-opacity="0" />
								</linearGradient>
							</defs>
							<polyline
								fill="none"
								stroke="#cba6f7"
								stroke-width="2"
								points={data.map((d, i) => `${(i / (data.length - 1)) * 280 + 10},${150 - (d.value / max * 130) - 10}`).join(' ')}
							/>
							{data.map((d, i) => (
								<circle
									key={i}
									cx={(i / (data.length - 1)) * 280 + 10}
									cy={150 - (d.value / max * 130) - 10}
									r="6"
									fill={colors[i % colors.length]}
									class="viz-point"
									events={{
										mouseenter: () => this.setHovered(i),
										mouseleave: () => this.setHovered(-1)
									}}
								/>
							))}
						</svg>
					)}
					{chartType === 'pie' && (
						<div class="viz-pie-chart">
							<svg viewBox="0 0 100 100" width="200" height="200">
								{(() => {
									const total = data.reduce((s, d) => s + d.value, 0);
									if (total === 0) return null;
									let acc = 0;
									return data.map((d, i) => {
										const start = acc / total * 360;
										const end = (acc + d.value) / total * 360;
										acc += d.value;
										const largeArc = end - start > 180 ? 1 : 0;
										const startRad = (start - 90) * Math.PI / 180;
										const endRad = (end - 90) * Math.PI / 180;
										const x1 = 50 + 40 * Math.cos(startRad);
										const y1 = 50 + 40 * Math.sin(startRad);
										const x2 = 50 + 40 * Math.cos(endRad);
										const y2 = 50 + 40 * Math.sin(endRad);
										return (
											<path
												key={i}
												d={`M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`}
												fill={colors[i % colors.length]}
												class="viz-slice"
												events={{
													mouseenter: () => this.setHovered(i),
													mouseleave: () => this.setHovered(-1)
												}}
												style={{ opacity: hoveredIndex === i ? 0.8 : 1 }}
											/>
										);
									});
								})()}
							</svg>
						</div>
					)}
				</div>
				<div class="viz-data-editor">
					{data.map((d, i) => (
						<div key={i} class="viz-data-row">
							<span class="viz-data-label">{d.label}</span>
							<input
								type="range"
								min="0"
								max="100"
								value={d.value}
								events={{ input: (e) => this.updateValue(i, e.target.value) }}
							/>
							<span class="viz-data-value">{d.value}</span>
						</div>
					))}
				</div>
			</div>
		);
	}
});

registerDemo('todo', TodoList);
registerDemo('snippet', CodeSnippet);
registerDemo('viz', DataViz);

const ExampleCard = component({
	name: 'ExampleCard',
	props: ['tag', 'tagClass', 'title', 'desc', 'code', 'demoType', 'containerId'],
	state: { mode: 'live', mountError: false, copiedCode: false },
	mounted() {
		if (this.getState().mode === 'live') {
			this._mountTimer = setTimeout(() => {
				this._mountDemo();
			}, 50);
		}
	},
	methods() {
		return {
			switchToLive() {
				if (this.getState().mode === 'live') return;
				playPop();
				this._unmountDemo();
				this.setState({ mode: 'live', mountError: false });
				// Mount after setState's re-render completes
				this._mountTimer = setTimeout(() => {
					if (this.getState().mode === 'live') {
						this._mountDemo();
					}
				}, 0);
			},
			switchToCode() {
				playPop();
				if (this._mountTimer) clearTimeout(this._mountTimer);
				this._unmountDemo();
				this.setState({ mode: 'code', mountError: false });
			},
			_mountDemo() {
				// Unmount any existing app first to allow clean re-mount
				if (this._app) {
					this._unmountDemo();
				}
				const container = document.getElementById(this.props.containerId);
				if (!container) {
					console.warn(`[ExamplesPage] Container #${this.props.containerId} not found for demo "${this.props.demoType}"`);
					return;
				}
				container.innerHTML = '';
				const { demoType } = this.props;
				try {
					const DemoComponent = getDemo(demoType);
					if (!DemoComponent) {
						console.warn(`[ExamplesPage] Demo type "${demoType}" not found in registry. Available: ${Object.keys(demoRegistry).join(', ')}`);
						this.setState({ mountError: true });
						return;
					}
					this._app = new App(DemoComponent, {});
					this._app.mount(this.props.containerId);
				} catch (err) {
					console.error('[ExamplesPage] Demo mount error:', err);
					this.setState({ mountError: true });
				}
			},
			_unmountDemo() {
				if (this._app) {
					this._app.unmount();
					this._app = null;
				}
				const container = document.getElementById(this.props.containerId);
				if (container) container.innerHTML = '';
			},
			copyCode(code) {
				copyToClipboard(code,
					() => {
						showToast('Code copied to clipboard', { type: 'success' });
						playChime();
					},
					() => {
						showToast('Failed to copy code', { type: 'error' });
					}
				);
			}
		};
	},
	unmounted() {
		if (this._mountTimer) clearTimeout(this._mountTimer);
		this._unmountDemo();
	},
	render() {
		const { mode, mountError } = this.getState();
		const { tag, tagClass, title, desc, code, containerId, demoType, difficulty } = this.props;
		return (
			<div class="example-card">
				<div class="example-card-header">
					<div class="example-card-meta">
						<span class={"example-tag" + (tagClass ? " " + tagClass : "")}>{tag}</span>
						{difficulty && <span class="example-difficulty">{difficulty}</span>}
					</div>
					<h3>{title}</h3>
					<p class="example-desc">{desc}</p>
				</div>
				<div class="example-toolbar">
					<div class="example-tabs">
						<button
							class={"example-tab" + (mode === 'code' ? " -active" : "")}
							events={{ click: () => this.switchToCode() }}
						>
							<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
							Code
						</button>
						<button
							class={"example-tab" + (mode === 'live' ? " -active" : "")}
							events={{ click: () => this.switchToLive() }}
						>
							<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
							Live
						</button>
					</div>
				</div>
				{mode === 'code' ? (
							<div class="code-block code-block-wrapper">
								<button
									class={`copy-btn ${this.getState().copiedCode ? '-copied' : ''}`}
									events={{ click: () => this.copyCode(code) }}
									aria-label="Copy code to clipboard"
								>
									{this.getState().copiedCode ? 'Copied!' : 'Copy'}
								</button>
								<div class="code-body">
									<pre><code class="language-javascript">{code}</code></pre>
								</div>
							</div>
				) : (
					<div class="live-demo-stage">
						{mountError && (
							<div class="demo-error">Failed to load demo</div>
						)}
						<div
							id={containerId}
							class="live-demo-container demo-loaded"
						></div>
					</div>
				)}
			</div>
		);
	}
});

const examplesData = [
	{
		id: 'kanban',
		tag: 'Full App',
		tagClass: '-green',
		title: 'Kanban Board',
		desc: 'Multi-column task manager with add, move, and delete.',
		demoType: 'kanban',
		containerId: 'example-kanban',
		category: 'productivity',
		code: `const KanbanBoard = component({
  name: 'KanbanBoard',
  state: {
    tasks: [...],
    nextId: 8, newTask: '', newPriority: 'medium'
  },
  methods() {
    return {
      moveTask(id, toColumn) {
        this.setState({
          tasks: this.getState().tasks.map(t =>
            t.id === id ? { ...t, column: toColumn } : t
          )
        });
      },
    };
  },
  render() { /* ... */ }
});`
	},
	{
		id: 'search',
		tag: 'Search',
		title: 'Real-Time Search',
		desc: 'Instant API reference search with filtering.',
		demoType: 'search',
		containerId: 'example-search',
		category: 'search',
		code: `const SearchDemo = component({
  name: 'SearchDemo',
  state: { query: '', results: [], allItems: [...] },
  methods() {
    return {
      search(query) {
        const q = query.toLowerCase();
        const results = q ? this.getState()
          .allItems.filter(item =>
            item.name.toLowerCase().includes(q))
          : [];
        this.setState({ query, results });
      }
    };
  },
  render() { /* ... */ }
});`
	},
	{
		id: 'clock',
		tag: 'Canvas',
		tagClass: '-violet',
		title: 'Analog Clock',
		desc: 'Live canvas-rendered clock with animated hands.',
		demoType: 'clock',
		containerId: 'example-clock',
		category: 'canvas',
		code: `const ClockDemo = component({
  name: 'ClockDemo',
  state: { time: '' },
  mounted() {
    this._draw();
    this._interval = setInterval(
      () => this._draw(), 1000);
  },
  unmounted() {
    clearInterval(this._interval);
  },
  render() {
    return <canvas id="demo-clock" />;
  }
});`
	},
	{
		id: 'pomodoro',
		tag: 'Timer',
		tagClass: '-peach',
		title: 'Pomodoro Timer',
		desc: 'Productivity timer with work/break modes and progress ring.',
		demoType: 'pomodoro',
		containerId: 'example-pomodoro',
		category: 'timer',
		code: `const PomodoroTimer = component({
  name: 'PomodoroTimer',
  state: { minutes: 25, seconds: 0,
    isRunning: false, mode: 'work' },
  methods() {
    return {
      toggleTimer() {
        if (this.getState().isRunning) {
          this._pause();
        } else {
          this._start();
        }
      },
    };
  },
  render() { /* ... */ }
});`
	},
	{
		id: 'expense',
		tag: 'Finance',
		tagClass: '-mint',
		title: 'Expense Tracker',
		desc: 'Budget management with categories, filters, and breakdowns.',
		demoType: 'expense',
		containerId: 'example-expense',
		category: 'finance',
		code: `const ExpenseTracker = component({
  name: 'ExpenseTracker',
  state: {
    items: [...],
    filter: 'all',
    newName: '', newAmount: '', newCategory: 'food'
  },
  methods() {
    return {
      addExpense() { /* ... */ },
      deleteExpense(id) { /* ... */ },
    };
  },
  render() { /* ... */ }
});`
	},
	{
		id: 'palette',
		tag: 'Design',
		tagClass: '-pink',
		title: 'Color Palette',
		desc: 'Generate beautiful palettes, lock colors, copy hex codes.',
		demoType: 'palette',
		containerId: 'example-palette',
		category: 'design',
		code: `const ColorPalette = component({
  name: 'ColorPalette',
  state: {
    colors: [
      { hex: '#cba6f7', locked: false },
      { hex: '#f5c2e7', locked: false },
    ],
    copied: null
  },
  methods() {
    return {
      generate() { /* ... */ },
      toggleLock(i) { /* ... */ },
      copyHex(hex) { /* ... */ },
    };
  },
  render() { /* ... */ }
});`
	},
	{
		id: 'markdown',
		tag: 'Editor',
		tagClass: '-sky',
		title: 'Markdown Preview',
		desc: 'Live markdown parser with split view and instant preview.',
		demoType: 'markdown',
		containerId: 'example-markdown',
		category: 'editor',
		code: `const MarkdownPreview = component({
  name: 'MarkdownPreview',
  state: { text: '# Hello...', mode: 'split' },
  methods() {
    return {
      parseMarkdown(text) {
        return text
          .replace(/# (.*)/, '<h1>$1</h1>')
          .replace(/\*\*(.*)\*\*/, '<b>$1</b>');
      }
    };
  },
  render() { /* ... */ }
});`
	},
	{
		id: 'password',
		tag: 'Security',
		tagClass: '-lavender',
		title: 'Password Generator',
		desc: 'Secure password creator with strength meter and options.',
		demoType: 'password',
		containerId: 'example-password',
		category: 'security',
		code: `const PasswordGenerator = component({
  name: 'PasswordGenerator',
  state: { length: 16, uppercase: true,
    lowercase: true, numbers: true,
    symbols: true, password: '', strength: 0 },
  mounted() { this.generate(); },
  methods() {
    return {
      generate() { /* crypto.getRandomValues */ },
      _calcStrength(pwd) { /* ... */ },
    };
  },
  render() { /* ... */ }
});`
	},
	{
		id: 'typing',
		tag: 'Game',
		tagClass: '-yellow',
		title: 'Typing Speed Test',
		desc: 'WPM calculator with real-time accuracy and best score.',
		demoType: 'typing',
		containerId: 'example-typing',
		category: 'game',
		code: `const TypingTest = component({
  name: 'TypingTest',
  state: {
    quote: 'The quick brown fox...',
    input: '', isRunning: false,
    wpm: 0, accuracy: 100,
    bestWpm: 0
  },
  methods() {
    return {
      start() { /* ... */ },
      onInput(text) { /* calc WPM */ },
    };
  },
  render() { /* ... */ }
});`
	},
	{
		id: 'weather',
		tag: 'Widget',
		tagClass: '-sky',
		title: 'Weather Widget',
		desc: 'Simulated weather for 5 cities with gradient backgrounds and animated transitions.',
		demoType: 'weather',
		containerId: 'example-weather',
		category: 'widget',
		difficulty: 'Beginner',
		code: `const WeatherWidget = component({
  name: 'WeatherWidget',
  state: { city: 'London', animating: false },
  methods() {
    return {
      setCity(city) {
        this.setState({ animating: true });
        setTimeout(() => {
          this.setState({ city, animating: false });
        }, 200);
      }
    };
  },
  render() {
    const { city } = this.getState();
    const data = weatherData[city];
    return (
      <div class="weather-demo">
        <div class="weather-card" style={{
          background: weatherGradients[data.condition]
        }}>
          <h3>{city}</h3>
          <div>{data.temp}°</div>
          <div>{data.condition}</div>
        </div>
      </div>
    );
  }
});`
	},
	{
		id: 'chart',
		tag: 'Chart',
		tagClass: '-lavender',
		title: 'Chart Demo',
		desc: 'Animated bar chart with multiple datasets, hover values, and smooth transitions.',
		demoType: 'chart',
		containerId: 'example-chart',
		category: 'chart',
		difficulty: 'Advanced',
		code: `const ChartDemo = component({
  name: 'ChartDemo',
  state: { dataset: 'Sales', hoveredIndex: -1, animated: false },
  mounted() {
    setTimeout(() => this.setState({ animated: true }), 100);
  },
  methods() {
    return {
      setDataset(name) {
        this.setState({ dataset: name, animated: false });
        setTimeout(() => this.setState({ animated: true }), 50);
      }
    };
  },
  render() {
    const { dataset, animated } = this.getState();
    const ds = chartDatasets[dataset];
    return (
      <div class="chart-demo">
        {Object.keys(chartDatasets).map(name => (
          <button events={{ click: () => this.setDataset(name) }}>
            {name}
          </button>
        ))}
        <div class="chart-bars">
          {ds.data.map((value, i) => (
            <div style={{ height: animated ? (value / max) * 100 + '%' : 0 }} />
          ))}
        </div>
      </div>
    );
  }
});`
	},
	{
		id: 'gallery',
		tag: 'Gallery',
		tagClass: '-mint',
		title: 'Image Gallery',
		desc: 'Grid gallery with category filters, lightbox modal, and lazy loading simulation.',
		demoType: 'gallery',
		containerId: 'example-gallery',
		category: 'gallery',
		difficulty: 'Beginner',
		code: `const ImageGallery = component({
  name: 'ImageGallery',
  state: { filter: 'all', lightbox: null, loaded: {} },
  methods() {
    return {
      setFilter(filter) { this.setState({ filter }); },
      openLightbox(id) { this.setState({ lightbox: id }); },
      closeLightbox() { this.setState({ lightbox: null }); },
      markLoaded(id) {
        this.setState({ loaded: { ...this.getState().loaded, [id]: true } });
      }
    };
  },
  render() {
    const { filter, lightbox, loaded } = this.getState();
    const filtered = filter === 'all'
      ? galleryImages
      : galleryImages.filter(img => img.category === filter);
    return (
      <div class="gallery-demo">
        <div class="gallery-filters">
          {['all', 'nature', 'city'].map(f => (
            <button events={{ click: () => this.setFilter(f) }}>
              {f}
            </button>
          ))}
        </div>
        <div class="gallery-grid">
          {filtered.map(img => (
            <div events={{ click: () => this.openLightbox(img.id) }}>
              {!loaded[img.id] && <div class="shimmer" />}
              <img src={img.src} onLoad={() => this.markLoaded(img.id)} />
            </div>
          ))}
        </div>
      </div>
    );
  }
});`
	},
	{
		id: 'chat',
		tag: 'Chat',
		tagClass: '-peach',
		title: 'Chat Interface',
		desc: 'Mock chat with bot responses, typing indicator, and message timestamps.',
		demoType: 'chat',
		containerId: 'example-chat',
		category: 'chat',
		difficulty: 'Intermediate',
		code: `const ChatInterface = component({
  name: 'ChatInterface',
  state: {
    messages: [...],
    input: '', isTyping: false, nextId: 4
  },
  methods() {
    return {
      sendMessage() {
        const { input, messages, nextId } = this.getState();
        this.setState({
          messages: [...messages, { id: nextId, text: input, sender: 'user' }],
          input: '', isTyping: true
        });
        setTimeout(() => {
          const response = botResponses[Math.floor(Math.random() * botResponses.length)];
          this.setState({
            messages: [...this.getState().messages, { text: response, sender: 'bot' }],
            isTyping: false
          });
        }, 1500);
      }
    };
  },
  render() {
    const { messages, input, isTyping } = this.getState();
    return (
      <div class="chat-demo">
        <div class="chat-messages">
          {messages.map(msg => (
            <div class={msg.sender}>{msg.text}</div>
          ))}
          {isTyping && <div class="typing-indicator">...</div>}
        </div>
        <input value={input} events={{ input: (e) => this.setInput(e.target.value) }} />
        <button events={{ click: () => this.sendMessage() }}>Send</button>
      </div>
    );
  }
});`
	},
	{
		id: 'calculator',
		tag: 'Math',
		tagClass: '-yellow',
		title: 'Calculator',
		desc: 'Fully functional arithmetic calculator with basic operations.',
		demoType: 'calculator',
		containerId: 'example-calculator',
		category: 'math',
		difficulty: 'Beginner',
		code: `const Calculator = component({
  name: 'Calculator',
  state: { display: '0', previousValue: null, operation: null },
  methods() {
    return {
      inputDigit(digit) {
        const { display } = this.getState();
        this.setState({ display: display === '0' ? String(digit) : display + digit });
      },
      performOperation(op) {
        const { display, previousValue, operation } = this.getState();
        const inputValue = parseFloat(display);
        if (previousValue === null) {
          this.setState({ previousValue: inputValue, operation: op, display: '0' });
        } else {
          const result = this.calculate(previousValue, inputValue, operation);
          this.setState({ display: String(result), previousValue: result, operation: op });
        }
      },
      calculate(a, b, op) {
        switch (op) {
          case '+': return a + b;
          case '-': return a - b;
          case '×': return a * b;
          case '÷': return b !== 0 ? a / b : 0;
          default: return b;
        }
      },
      clear() {
        this.setState({ display: '0', previousValue: null, operation: null });
      }
    };
  },
  render() {
    return (
      <div class="calc-demo">
        <div class="calc-display">{this.getState().display}</div>
        <div class="calc-buttons">
          <button events={{ click: () => this.clear() }}>C</button>
          <button events={{ click: () => this.performOperation('÷') }}>÷</button>
          <button events={{ click: () => this.performOperation('×') }}>×</button>
          <button events={{ click: () => this.performOperation('-') }}>-</button>
          {[7, 8, 9, 4, 5, 6, 1, 2, 3, 0].map(n => (
            <button key={n} events={{ click: () => this.inputDigit(n) }}>{n}</button>
          ))}
          <button events={{ click: () => this.performOperation('+') }}>+</button>
          <button events={{ click: () => this.performOperation('=') }}>=</button>
        </div>
      </div>
    );
  }
});`
	},
	{
		id: 'music',
		tag: 'Audio',
		tagClass: '-pink',
		title: 'Music Player',
		desc: 'Simulated music player with playlist, progress bar, and volume control.',
		demoType: 'music',
		containerId: 'example-music',
		category: 'audio',
		difficulty: 'Intermediate',
		code: `const MusicPlayer = component({
  name: 'MusicPlayer',
  state: { isPlaying: false, currentTrack: 0, progress: 0, volume: 70 },
  methods() {
    return {
      togglePlay() { this.setState({ isPlaying: !this.getState().isPlaying }); },
      nextTrack() {
        this.setState({ currentTrack: (this.getState().currentTrack + 1) % 5, progress: 0 });
      },
      prevTrack() {
        this.setState({ currentTrack: (this.getState().currentTrack - 1 + 5) % 5, progress: 0 });
      },
      setVolume(vol) { this.setState({ volume: vol }); }
    };
  },
  render() {
    const { isPlaying, currentTrack, progress, volume } = this.getState();
    const tracks = [
      { title: 'Midnight City', artist: 'M83' },
      { title: 'Blinding Lights', artist: 'The Weeknd' },
      { title: 'Levitating', artist: 'Dua Lipa' }
    ];
    return (
      <div class="music-demo">
        <div class="music-info">
          <div class="music-title">{tracks[currentTrack].title}</div>
          <div class="music-artist">{tracks[currentTrack].artist}</div>
        </div>
        <div class="music-progress">
          <div class="music-progress-bar">
            <div style={{ width: progress + '%' }} />
          </div>
        </div>
        <div class="music-controls">
          <button events={{ click: () => this.prevTrack() }}>⏮</button>
          <button events={{ click: () => this.togglePlay() }}>
            {isPlaying ? '⏸' : '▶'}
          </button>
          <button events={{ click: () => this.nextTrack() }}>⏭</button>
        </div>
        <input type="range" min="0" max="100" value={volume}
          events={{ input: (e) => this.setVolume(e.target.value) }} />
      </div>
    );
  }
});`
	},
	{
		id: 'qr',
		tag: 'Utility',
		tagClass: '-lavender',
		title: 'QR Generator',
		desc: 'Generate QR code patterns from any text or URL input.',
		demoType: 'qr',
		containerId: 'example-qr',
		category: 'utility',
		difficulty: 'Beginner',
		code: `const QRGenerator = component({
  name: 'QRGenerator',
  state: { text: 'https://literaljs.com' },
  mounted() { this.generateQR(); },
  methods() {
    return {
      setText(text) { this.setState({ text }); },
      generateQR() {
        const canvas = document.getElementById('qr-canvas');
        const ctx = canvas.getContext('2d');
      }
    };
  },
  render() {
    return (
      <div class="qr-demo">
        <input value={this.getState().text}
          events={{ input: (e) => this.setText(e.target.value) }} />
        <button events={{ click: () => this.generateQR() }}>Generate</button>
        <canvas id="qr-canvas" />
      </div>
    );
  }
});`
	},
	{
		id: 'todo',
		tag: 'Task',
		tagClass: '-mint',
		title: 'Todo List',
		desc: 'Full-featured task manager with priorities, filters, editing, and progress tracking.',
		demoType: 'todo',
		containerId: 'example-todo',
		category: 'productivity',
		difficulty: 'Intermediate',
		code: `const TodoList = component({
  name: 'TodoList',
  state: {
    todos: [...],
    filter: 'all', editingId: null
  },
  methods() {
    return {
      toggleTodo(id) { /* ... */ },
      addTodo() { /* ... */ },
      startEdit(todo) { /* ... */ },
      saveEdit() { /* ... */ }
    };
  },
  render() { /* ... */ }
});`
	},
	{
		id: 'snippet',
		tag: 'Code',
		tagClass: '-sky',
		title: 'Code Snippet',
		desc: 'Live syntax highlighting with editable code and copy functionality.',
		demoType: 'snippet',
		containerId: 'example-snippet',
		category: 'editor',
		difficulty: 'Intermediate',
		code: `const CodeSnippet = component({
  name: 'CodeSnippet',
  state: { code: '...', language: 'javascript' },
  methods() {
    return {
      highlight(text) {
        return text.replace(/keyword/g, '<span class="token">$1</span>');
      }
    };
  },
  render() { /* ... */ }
});`
	},
	{
		id: 'viz',
		tag: 'Data',
		tagClass: '-lavender',
		title: 'Data Visualization',
		desc: 'Interactive bar, line, and pie charts with live data editing.',
		demoType: 'viz',
		containerId: 'example-viz',
		category: 'chart',
		difficulty: 'Advanced',
		code: `const DataViz = component({
  name: 'DataViz',
  state: {
    chartType: 'bar',
    data: [{ label: 'Jan', value: 45 }, ...]
  },
  methods() {
    return {
      setChartType(type) { /* ... */ },
      updateValue(i, v) { /* ... */ }
    };
  },
  render() { /* SVG charts */ }
});`
	}
];

function filterBySubstring(query, items) {
	if (!query) return items;
	const q = query.toLowerCase();
	return items.filter(item => {
		const text = `${item.title} ${item.desc} ${item.tag} ${item.category}`.toLowerCase();
		return text.includes(q);
	});
}

// ═══════════════════════════════════════════════════
// ExamplesPage
// ═══════════════════════════════════════════════════
export default component({
	name: 'ExamplesPage',
	state: {
		searchQuery: '',
		selectedCategory: 'all'
	},
	methods() {
		return {
			setSearch(query) {
				if (this._searchTimer) clearTimeout(this._searchTimer);
				this._searchTimer = setTimeout(() => {
					this.setState({ searchQuery: query });
				}, 200);
			},
			setCategory(category) {
				playPop();
				this.setState({ selectedCategory: category });
			}
		};
	},
	unmounted() {
		if (this._searchTimer) clearTimeout(this._searchTimer);
	},
	render() {
		const { searchQuery, selectedCategory } = this.getState();

		let filteredExamples = examplesData;
		if (searchQuery) {
			filteredExamples = filterBySubstring(searchQuery, examplesData);
		}
		if (selectedCategory !== 'all') {
			filteredExamples = filteredExamples.filter(item => item.category === selectedCategory);
		}

		const categories = [
			{ id: 'all', label: 'All' },
			{ id: 'productivity', label: 'Full App' },
			{ id: 'search', label: 'Search' },
			{ id: 'canvas', label: 'Canvas' },
			{ id: 'timer', label: 'Timer' },
			{ id: 'finance', label: 'Finance' },
			{ id: 'design', label: 'Design' },
			{ id: 'editor', label: 'Editor' },
			{ id: 'security', label: 'Security' },
			{ id: 'game', label: 'Game' },
			{ id: 'widget', label: 'Widget' },
			{ id: 'chart', label: 'Chart' },
			{ id: 'gallery', label: 'Gallery' },
			{ id: 'chat', label: 'Chat' },
			{ id: 'math', label: 'Math' },
			{ id: 'audio', label: 'Audio' },
			{ id: 'utility', label: 'Utility' },
			{ id: 'todo', label: 'Task' },
			{ id: 'snippet', label: 'Code' },
			{ id: 'viz', label: 'Data' }
		];

		return (
			<div class="page-examples">
				<section class="examples">
					<div class="examples-hero">
						<div class="examples-hero-badge">
							<span class="live-dot"></span>
							<span>Live Demos</span>
						</div>
						<h1>Interactive Examples</h1>
						<p class="examples-hero-subtitle">21 production-ready components built with LiteralJS v8.1.11</p>
					</div>

					<div class="examples-toolbar">
						<div class="toolbar-left">
							<label for="examples-search" class="visually-hidden">Search examples</label>
							<SearchBar
								onSearch={(q) => this.setSearch(q)}
								placeholder="Search examples..."
								id="examples-search"
							/>
							<span class="toolbar-count">{filteredExamples.length} / {examplesData.length}</span>
						</div>
						<div class="toolbar-center">
							<div class="category-filters">
								{categories.map(cat => (
									<button
										key={cat.id}
										class={"category-btn" + (selectedCategory === cat.id ? " -active" : "")}
										events={{ click: () => this.setCategory(cat.id) }}
									>
										{cat.label}
									</button>
								))}
							</div>
						</div>
					</div>

					<div class="example-grid">
						{filteredExamples.map(example => (
							<ExampleCard
								key={example.id}
								tag={example.tag}
								tagClass={example.tagClass}
								title={example.title}
								desc={example.desc}
								demoType={example.demoType}
								containerId={example.containerId}
								code={example.code}
								difficulty={example.difficulty}
							/>
						))}
					</div>

					{filteredExamples.length === 0 && (
					<div class="no-results">
						<div class="no-results-icon">
							<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
						</div>
						<h3>No examples found</h3>
						<p>Try adjusting your search or category filter</p>
						<button class="no-results-cta" events={{ click: () => this.setCategory('all') }}>View all examples</button>
					</div>
					)}
				</section>
			</div>
		);
	}
});